import classNames from "classnames";
import React from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaSkeleton from "../kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSmallText from "../typography/SmallText/SmallText";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-template-card.scss";

/**
 * A gallery card: cover artwork, an optional badge over it, a name and two lines
 * of description, with its actions rising over the ARTWORK on hover so the text
 * you are reading to decide stays visible.
 *
 * Every card is the same height whether or not its description fills two lines —
 * a gallery of ragged cards was the thing this replaced.
 */

export interface KuikaTemplateCardAction {
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

export interface KuikaTemplateCardProps {
  title: string;
  description?: string;
  imageUrl?: string;
  /**
   * Cover artwork for catalogs that have none. A glyph on a tinted band beats the
   * default thumbnail, which is AI-branded and says the wrong thing anywhere else.
   * Ignored when `imageUrl` is set.
   */
  coverIcon?: React.ReactNode;
  /** Small frosted chip over the cover — e.g. the model behind a template. */
  badge?: string;
  selected?: boolean;
  onClick?: () => void;
  primaryAction?: KuikaTemplateCardAction;
  secondaryAction?: KuikaTemplateCardAction;
  className?: string;
  "data-testid"?: string;
}

const FALLBACK_COVER = "/images/ai-template-thumb.png";

const KuikaTemplateCard = ({
  title,
  description,
  imageUrl,
  coverIcon,
  badge,
  selected = false,
  onClick,
  primaryAction,
  secondaryAction,
  className,
  "data-testid": dataTestId
}: KuikaTemplateCardProps) => (
  <div
    className={classNames("kuika-template-card", { "kuika-template-card--selected": selected }, className)}
    role="button"
    tabIndex={0}
    data-testid={dataTestId}
    onClick={onClick}
    // The card holds its own buttons, so it cannot BE a button — give it the
    // keyboard behaviour a button would have had.
    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      onClick?.();
    }}
  >
    <span
      className={classNames("kuika-template-card__cover", {
        "kuika-template-card__cover--glyph": !imageUrl && !!coverIcon
      })}
    >
      {/* Decorative: the title below already names the card. */}
      {!imageUrl && coverIcon ? coverIcon : <img src={imageUrl || FALLBACK_COVER} alt="" />}
      {badge && <span className="kuika-template-card__badge">{badge}</span>}
      <span className="kuika-template-card__check">
        <KuikaSvg name="check" width={12} height={12} />
      </span>
      {(primaryAction || secondaryAction) && (
        <span className="kuika-template-card__actions" onClick={(e) => e.stopPropagation()}>
          {primaryAction && (
            <KuikaButton type="primary" size="sm" onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
              {primaryAction.label}
            </KuikaButton>
          )}
          {secondaryAction && (
            <KuikaButton
              type="secondary"
              size="sm"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </KuikaButton>
          )}
        </span>
      )}
    </span>
    <div className="kuika-template-card__content">
      <KuikaTextMedium className="kuika-template-card__title">{title}</KuikaTextMedium>
      {description && <KuikaSmallText className="kuika-template-card__description">{description}</KuikaSmallText>}
    </div>
  </div>
);

export default KuikaTemplateCard;

export interface KuikaTemplateCardGridProps {
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

/** Responsive grid whose rows are exactly one card tall. */
export const KuikaTemplateCardGrid = ({
  children,
  className,
  "data-testid": dataTestId
}: KuikaTemplateCardGridProps) => (
  <div className={classNames("kuika-template-card-grid", className)} data-testid={dataTestId}>
    {children}
  </div>
);

export interface KuikaTemplateCardSkeletonProps {
  /** How many placeholders to draw while the catalog loads. */
  count?: number;
}

/**
 * Loading placeholder in the card's own proportions — cover band, name, two
 * description lines — so the swap to real content doesn't jump.
 */
export const KuikaTemplateCardSkeleton = ({ count = 8 }: KuikaTemplateCardSkeletonProps) => (
  <>
    {Array.from({ length: count }).map((_, index: number) => (
      <div className="kuika-template-card kuika-template-card--skeleton" key={index} aria-hidden="true">
        <span className="kuika-template-card__cover">
          <KuikaSkeleton width="100%" height="100%" className="kuika-template-card__cover-skeleton" />
        </span>
        <div className="kuika-template-card__content">
          <KuikaSkeleton width="60%" height={14} />
          <KuikaSkeleton width="100%" height={10} />
          <KuikaSkeleton width="80%" height={10} />
        </div>
      </div>
    ))}
  </>
);
